import React, { useState, useEffect, useRef } from "react";
import { ArrowDown, ArrowUp, ChevronDown, Plus, ShieldCheck, Split, Trash2, Users } from "lucide-react";
import { CompactButton } from "../../Button/Buttons";
import FormInfo from "../../Inputs/FormInfo";
import SharedInput from "../../Inputs/Input";
import SharedSelect from "../../Inputs/Select";
import SharedTextArea from "../../Inputs/TextArea";
import Toggle from "../../shared/Toggle";
import JsonView from "./JsonView";
import { AUTH_FORMAT_OPTIONS } from "../../../utils/agentPolicyConnection";

const RISK_LEVEL_OPTIONS = [
  { value: "SAFE", label: "Safe" },
  { value: "MEDIUM", label: "Medium" },
  { value: "HIGH_RISK", label: "High risk" }
];

const ACTION_OPTIONS = [
  { value: "allow", label: "Allow" },
  { value: "require_approval", label: "Require approval" },
  { value: "block", label: "Block" }
];

const RULE_RISK_OPTIONS = [
  { value: "", label: "Intent risk" },
  ...RISK_LEVEL_OPTIONS,
  { value: "BLOCKED", label: "Blocked" }
];

const RISK_BADGE_CLASS = {
  SAFE: "bg-fill-emerald-50 text-ink-emerald-700 dark:bg-fill-emerald-900/30 dark:text-ink-emerald-300",
  MEDIUM: "bg-fill-hex-fffbeb text-ink-amber-700 dark:bg-fill-hex-78350f/30 dark:text-ink-amber-300",
  HIGH_RISK: "bg-fill-red-50 text-ink-red-700 dark:bg-fill-red-900/30 dark:text-ink-red-300"
};

const STEP_LABEL_CLASS = "rounded px-1.5 py-0.5 text-[10px] font-semibold tracking-wide";
const BRANCH_CLASS = "border-l-2 border-edge-gray-200 pl-3 dark:border-edge-gray-600";

const ACTION_BADGE_CLASS = {
  allow: "bg-fill-emerald-50 text-ink-emerald-700 dark:bg-fill-emerald-900/30 dark:text-ink-emerald-300",
  require_approval: "bg-fill-hex-fffbeb text-ink-amber-700 dark:bg-fill-hex-78350f/30 dark:text-ink-amber-300",
  block: "bg-fill-red-50 text-ink-red-700 dark:bg-fill-red-900/30 dark:text-ink-red-300"
};

const formatRange = (label, min, max) => {
  if (min == null && max == null) return null;
  if (min != null && max != null) return `${label} ${min}–${max}`;
  if (min != null) return `${label} ≥${min}`;
  return `${label} ≤${max}`;
};

const describeFallbackCondition = (rule) => {
  const parts = [
    formatRange("confidence", rule.confidence_min, rule.confidence_max),
    formatRange("severity", rule.severity_min, rule.severity_max)
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(" · ") : "Any confidence or severity";
};

// Firestore timestamps carry a UTC offset; policy-file timestamps historically did not,
// so pin offset-less values to UTC rather than letting the browser read them as local time.
const parseTimestamp = (isoString) => {
  if (!isoString) return null;
  const normalized = /(?:Z|[+-]\d{2}:?\d{2})$/.test(isoString) ? isoString : `${isoString}Z`;
  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatTimestamp = (isoString) => {
  const parsed = parseTimestamp(isoString);
  if (!parsed) return null;
  const formatted = parsed.toLocaleString("en-GB", {
    timeZone: "UTC",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false
  });
  return `${formatted} UTC`;
};

const Input = (props) => <SharedInput compact {...props} />;
const Select = (props) => <SharedSelect compact {...props} />;
const TextArea = (props) => <SharedTextArea compact {...props} />;

/**
 * PolicyFormView - Minimal form interface for policy.json editing
 * Focuses on common fields, complex nested structures edited in JSON view
 */
const PolicyFormView = ({ 
  value, 
  onChange, 
  readOnly = false, 
  mode = "form",
  onModeSwitch,
  supportsFormView = true,
  onError,
  fileType = "policy",
  policyAtom = null,
  // Connection details are edited on the Add/Edit Agent main form; they stay in
  // qz_pol and still round-trip through this view's state when hidden.
  connectionManagedExternally = false
}) => {
  const policyBaseRef = useRef({ qz_pol: {}, initial_data: {} });
  const [formData, setFormData] = useState({
    mcp_connection: "",
    mcp_auth_token: "",
    auth_format: "raw",
    auth_header: "",
    provider: "",
    per_user_limit: 100,
    per_org_limit: 1000,
    version: "1.0.0",
    allowed_roles: "all",
    safe_status: "SAFE",
    risk_score: 0,
    last_verified_at: ""
  });
  
  const [intents, setIntents] = useState([
    {
      name: "read",
      risk_level: "SAFE",
      requires_approval: false,
      description: "Read operations",
      default_action: "allow",
      minimum_rule_confidence: 0.8,
      rules: []
    }
  ]);
  
  const [fallbackDefaultAction, setFallbackDefaultAction] = useState("require_approval");
  const [fallbackRules, setFallbackRules] = useState([
    {
      action: "require_approval",
      risk_level: "HIGH_RISK",
      confirmation_text: "⚠️ UNKNOWN OPERATION",
      confidence_min: 0.8,
      confidence_max: null,
      severity_min: 0.5,
      severity_max: null
    },
    {
      action: "block",
      risk_level: "",
      reason: "Intent unclear - low confidence",
      confidence_min: null,
      confidence_max: 0.6,
      severity_min: null,
      severity_max: null
    }
  ]);
  
  const [expandedFallbackRules, setExpandedFallbackRules] = useState(new Set()); // fallback rules start collapsed to keep the list scannable
  const [expandedIntents, setExpandedIntents] = useState(new Set()); // intents start collapsed to keep the list scannable

  // Parse value on mount/change
  useEffect(() => {
    if (!value) return;
    
    try {
      const parsed = JSON.parse(value);
      const qz_pol = parsed.qz_pol || {};
      policyBaseRef.current = parsed;
      
      setFormData({
        mcp_connection: qz_pol.mcp_connection || "",
        mcp_auth_token: qz_pol.mcp_auth_token || "",
        auth_format: qz_pol.auth_format || "raw",
        auth_header: qz_pol.auth_header || "",
        provider: qz_pol.provider || "",
        per_user_limit: qz_pol.rate_limits?.per_user || 100,
        per_org_limit: qz_pol.rate_limits?.per_org || 1000,
        version: qz_pol.version || "1.0.0",
        allowed_roles: Array.isArray(qz_pol.allowed_roles) ? qz_pol.allowed_roles.join(", ") : "all",
        safe_status: parsed.initial_data?.safe_status || "SAFE",
        risk_score: parsed.initial_data?.risk_score || 0,
        last_verified_at: parsed.initial_data?.last_verified_at || ""
      });
      
      // Parse intents
      if (qz_pol.allowed_intents) {
        const parsedIntents = Object.entries(qz_pol.allowed_intents).map(([name, config]) => ({
          name,
          risk_level: config.risk_level || "SAFE",
          requires_approval: config.requires_approval || false,
          description: config.description || "",
          default_action: config.default_action || (config.requires_approval ? "require_approval" : "allow"),
          minimum_rule_confidence: config.minimum_rule_confidence ?? 0.8,
          rules: Array.isArray(config.rules) ? config.rules.map(rule => ({
            ...rule,
            id: rule.id || "",
            condition: typeof rule.condition === "string" ? rule.condition : "",
            action: rule.action || "require_approval",
            risk_level: rule.risk_level || "",
            reason: rule.reason || ""
          })) : [],
          _rawConfig: config
        }));
        setIntents(parsedIntents);
      }
      
      // Parse fallback policy
      if (qz_pol.fallback_policy?.unknown_intents) {
        const fb = qz_pol.fallback_policy.unknown_intents;
        setFallbackDefaultAction(fb.default_action || "require_approval");
        
        if (fb.rules && Array.isArray(fb.rules)) {
          const parsedRules = fb.rules.map(rule => ({
            _rawRule: rule,
            action: rule.action || "require_approval",
            risk_level: rule.risk_level || "",
            confirmation_text: rule.confirmation_text || "",
            reason: rule.reason || "",
            confidence_min: rule.condition?.confidence?.min ?? null,
            confidence_max: rule.condition?.confidence?.max ?? null,
            severity_min: rule.condition?.severity_score?.min ?? null,
            severity_max: rule.condition?.severity_score?.max ?? null
          }));
          setFallbackRules(parsedRules);
        }
      }
    } catch (err) {
      // Invalid JSON - stay in current state
    }
  }, [value]);

  // Sync form to parent
  const syncToParent = (newData, currentIntents = intents, currentFallbackDefaultAction = fallbackDefaultAction, currentFallbackRules = fallbackRules) => {
    // Parse allowed_roles from comma-separated string
    const rolesArray = newData.allowed_roles
      .split(",")
      .map(r => r.trim())
      .filter(r => r.length > 0);

    // Build allowed_intents from intents array
    const allowed_intents = {};
    currentIntents.forEach(intent => {
      if (intent.name) {
        const intentConfig = {
          ...(intent._rawConfig || {}),
          risk_level: intent.risk_level,
          requires_approval: intent.requires_approval,
          description: intent.description
        };
        if (intent.rules.length > 0) {
          intentConfig.default_action = intent.default_action;
          intentConfig.minimum_rule_confidence = intent.minimum_rule_confidence;
          intentConfig.rules = intent.rules.map(rule => ({
            id: rule.id,
            condition: rule.condition,
            action: rule.action,
            ...(rule.risk_level && { risk_level: rule.risk_level }),
            ...(rule.reason && { reason: rule.reason })
          }));
        } else {
          delete intentConfig.rules;
          delete intentConfig.default_action;
          delete intentConfig.minimum_rule_confidence;
        }
        allowed_intents[intent.name] = intentConfig;
      }
    });
    
    // Build fallback_policy from fallback state
    const rules = currentFallbackRules.map(rule => {
      const ruleObj = {
        ...(rule._rawRule || {}),
        condition: {},
        action: rule.action
      };
      
      // Build condition object
      if (rule.confidence_min !== null || rule.confidence_max !== null) {
        ruleObj.condition.confidence = {};
        if (rule.confidence_min !== null) ruleObj.condition.confidence.min = rule.confidence_min;
        if (rule.confidence_max !== null) ruleObj.condition.confidence.max = rule.confidence_max;
      }
      if (rule.severity_min !== null || rule.severity_max !== null) {
        ruleObj.condition.severity_score = {};
        if (rule.severity_min !== null) ruleObj.condition.severity_score.min = rule.severity_min;
        if (rule.severity_max !== null) ruleObj.condition.severity_score.max = rule.severity_max;
      }
      
      // Add optional fields
      if (rule.risk_level) ruleObj.risk_level = rule.risk_level;
      if (rule.confirmation_text) ruleObj.confirmation_text = rule.confirmation_text;
      if (rule.reason) ruleObj.reason = rule.reason;
      
      return ruleObj;
    });

    const policy = {
      qz_pol: {
        ...(policyBaseRef.current.qz_pol || {}),
        mcp_connection: newData.mcp_connection,
        mcp_auth_token: newData.mcp_auth_token,
        auth_format: newData.auth_format,
        ...(newData.auth_header && { auth_header: newData.auth_header }),
        provider: newData.provider,
        allowed_intents,
        fallback_policy: {
          unknown_intents: {
            default_action: currentFallbackDefaultAction,
            rules
          }
        },
        rate_limits: {
          per_user: newData.per_user_limit,
          per_org: newData.per_org_limit
        },
        allowed_roles: rolesArray,
        version: newData.version
      },
      initial_data: {
        ...(policyBaseRef.current.initial_data || {}),
        safe_status: newData.safe_status,
        risk_score: newData.risk_score,
        last_verified_at: newData.last_verified_at || null
      }
    };

    onChange(JSON.stringify(policy, null, 2));
  };

  const handleChange = (field, value) => {
    const newData = { ...formData, [field]: value };
    setFormData(newData);
    syncToParent(newData);
  };

  const policyMetadataItems = [
    { label: "Last verified", value: formatTimestamp(formData.last_verified_at) },
    policyAtom && {
      label: "Policy ATOM",
      value: `…${policyAtom.slice(-16)}`,
      title: policyAtom,
      mono: true
    }
  ].filter((item) => item && item.value);

  const handleIntentChange = (index, field, value) => {
    const newIntents = [...intents];
    newIntents[index] = { ...newIntents[index], [field]: value };
    setIntents(newIntents);
    syncToParent(formData, newIntents);
  };

  const addIntent = () => {
    const newIntents = [...intents, {
      name: "",
      risk_level: "SAFE",
      requires_approval: false,
      description: "",
      default_action: "allow",
      minimum_rule_confidence: 0.8,
      rules: []
    }];
    setIntents(newIntents);
    setExpandedIntents(prev => new Set(prev).add(newIntents.length - 1));
    syncToParent(formData, newIntents);
  };

  const removeIntent = (index) => {
    const newIntents = intents.filter((_, i) => i !== index);
    setIntents(newIntents);
    setExpandedIntents(prev => {
      const next = new Set();
      prev.forEach(expandedIndex => {
        if (expandedIndex < index) next.add(expandedIndex);
        else if (expandedIndex > index) next.add(expandedIndex - 1);
      });
      return next;
    });
    syncToParent(formData, newIntents);
  };

  const handleIntentRuleChange = (intentIndex, ruleIndex, field, value) => {
    const newIntents = [...intents];
    const newRules = [...newIntents[intentIndex].rules];
    newRules[ruleIndex] = { ...newRules[ruleIndex], [field]: value };
    newIntents[intentIndex] = { ...newIntents[intentIndex], rules: newRules };
    setIntents(newIntents);
    syncToParent(formData, newIntents);
  };

  const addIntentRule = (intentIndex) => {
    const newIntents = [...intents];
    const intent = newIntents[intentIndex];
    const prefix = (intent.name || "intent").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
    const existingRuleIds = new Set(intent.rules.map(rule => rule.id));
    let sequence = intent.rules.length + 1;
    let ruleId = `${prefix}_rule_${sequence}`;
    while (existingRuleIds.has(ruleId)) {
      sequence += 1;
      ruleId = `${prefix}_rule_${sequence}`;
    }
    newIntents[intentIndex] = {
      ...intent,
      rules: [...intent.rules, {
        id: ruleId,
        condition: "",
        action: "require_approval",
        risk_level: "",
        reason: ""
      }]
    };
    setIntents(newIntents);
    setExpandedIntents(prev => new Set(prev).add(intentIndex));
    syncToParent(formData, newIntents);
  };

  const removeIntentRule = (intentIndex, ruleIndex) => {
    const newIntents = [...intents];
    newIntents[intentIndex] = {
      ...newIntents[intentIndex],
      rules: newIntents[intentIndex].rules.filter((_, index) => index !== ruleIndex)
    };
    setIntents(newIntents);
    syncToParent(formData, newIntents);
  };

  const moveIntentRule = (intentIndex, ruleIndex, direction) => {
    const targetIndex = ruleIndex + direction;
    if (targetIndex < 0 || targetIndex >= intents[intentIndex].rules.length) return;
    const newIntents = [...intents];
    const newRules = [...newIntents[intentIndex].rules];
    [newRules[ruleIndex], newRules[targetIndex]] = [newRules[targetIndex], newRules[ruleIndex]];
    newIntents[intentIndex] = { ...newIntents[intentIndex], rules: newRules };
    setIntents(newIntents);
    syncToParent(formData, newIntents);
  };
  
  const handleFallbackDefaultActionChange = (value) => {
    setFallbackDefaultAction(value);
    syncToParent(formData, intents, value, fallbackRules);
  };
  
  const handleFallbackRuleChange = (index, field, value) => {
    const newRules = [...fallbackRules];
    newRules[index] = { ...newRules[index], [field]: value };
    setFallbackRules(newRules);
    syncToParent(formData, intents, fallbackDefaultAction, newRules);
  };
  
  const addFallbackRule = () => {
    const newRules = [...fallbackRules, {
      action: "require_approval",
      risk_level: "",
      confirmation_text: "",
      reason: "",
      confidence_min: null,
      confidence_max: null,
      severity_min: null,
      severity_max: null
    }];
    setFallbackRules(newRules);
    setExpandedFallbackRules(prev => new Set(prev).add(newRules.length - 1));
    syncToParent(formData, intents, fallbackDefaultAction, newRules);
  };
  
  const removeFallbackRule = (index) => {
    const newRules = fallbackRules.filter((_, i) => i !== index);
    setFallbackRules(newRules);
    setExpandedFallbackRules(prev => {
      const next = new Set();
      prev.forEach(expandedIndex => {
        if (expandedIndex < index) next.add(expandedIndex);
        else if (expandedIndex > index) next.add(expandedIndex - 1);
      });
      return next;
    });
    syncToParent(formData, intents, fallbackDefaultAction, newRules);
  };

  const moveFallbackRule = (index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= fallbackRules.length) return;
    const newRules = [...fallbackRules];
    [newRules[index], newRules[targetIndex]] = [newRules[targetIndex], newRules[index]];
    setFallbackRules(newRules);
    setExpandedFallbackRules(prev => {
      const next = new Set(prev);
      const hadIndex = prev.has(index);
      const hadTarget = prev.has(targetIndex);
      next.delete(index);
      next.delete(targetIndex);
      if (hadIndex) next.add(targetIndex);
      if (hadTarget) next.add(index);
      return next;
    });
    syncToParent(formData, intents, fallbackDefaultAction, newRules);
  };
  
  const toggleFallbackRuleExpand = (index) => {
    setExpandedFallbackRules(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const toggleIntentExpand = (index) => {
    setExpandedIntents(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  return (
    <div className="space-y-4">
      {/* View Mode Toggle */}
      <div className="flex items-center justify-between mb-3">
        <label className="block text-sm font-semibold text-ink-gray-700 dark:text-ink-gray-300">
          {mode === "form" ? "Policy Configuration" : "JSON Editor"}
        </label>
        
        {supportsFormView && (
          <div className="flex items-center gap-0.5 bg-fill-gray-100 dark:bg-fill-gray-800 rounded-md p-0.5">
            <button
              type="button"
              onClick={() => onModeSwitch("form")}
              disabled={mode === "form"}
              className={`px-2 py-1 text-sm font-mono rounded transition-all ${
                mode === "form"
                  ? "bg-fill-white dark:bg-fill-gray-700 text-ink-primary shadow-sm"
                  : "text-ink-gray-500 dark:text-ink-gray-400 hover:text-ink-gray-700 dark:hover:text-ink-gray-300"
              }`}
            >
              [form]
            </button>
            <button
              type="button"
              onClick={() => onModeSwitch("json")}
              disabled={mode === "json"}
              className={`px-2 py-1 text-sm font-mono rounded transition-all ${
                mode === "json"
                  ? "bg-fill-white dark:bg-fill-gray-700 text-ink-primary shadow-sm"
                  : "text-ink-gray-500 dark:text-ink-gray-400 hover:text-ink-gray-700 dark:hover:text-ink-gray-300"
              }`}
            >
              [json]
            </button>
          </div>
        )}
      </div>

      {/* Content: Form or JSON */}
      {mode === "form" ? (
        <div className="space-y-4 px-1">
        {connectionManagedExternally ? (
          <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400">
            Provider and MCP connection details are edited on the agent form above.
          </p>
        ) : (
          <>
        {/* Connection: which provider, and where it lives */}
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-ink-gray-600 dark:text-ink-gray-400 mb-1">
              Provider
            </label>
            <Input
              type="text"
              value={formData.provider}
              onChange={(e) => handleChange('provider', e.target.value)}
              disabled={readOnly}
              placeholder="Provider identifier"
            />
          </div>

          <div className="col-span-2">
            <label className="block text-xs font-medium text-ink-gray-600 dark:text-ink-gray-400 mb-1">
              MCP Connection <span className="text-ink-red-500">*</span>
            </label>
            <Input
              type="text"
              value={formData.mcp_connection}
              onChange={(e) => handleChange('mcp_connection', e.target.value)}
              disabled={readOnly}
              placeholder="https://mcp.example.com/mcp"
            />
          </div>
        </div>

        {/* Authentication: how the credential is sent, then the credential itself */}
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-ink-gray-600 dark:text-ink-gray-400 mb-1">
              Auth Format <span className="text-ink-red-500">*</span>
            </label>
            <Select
              value={formData.auth_format}
              onChange={(nextValue) => handleChange('auth_format', nextValue)}
              options={AUTH_FORMAT_OPTIONS}
              disabled={readOnly}
            />
          </div>

          <div className="col-span-2">
            <label className="block text-xs font-medium text-ink-gray-600 dark:text-ink-gray-400 mb-1">
              Auth Header <span className="text-ink-gray-400">(optional)</span>
            </label>
            <Input
              type="text"
              value={formData.auth_header}
              onChange={(e) => handleChange('auth_header', e.target.value)}
              disabled={readOnly}
              placeholder="e.g., x-api-key"
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-ink-gray-600 dark:text-ink-gray-400 mb-1">
            MCP Auth Token
          </label>
          <TextArea
            value={formData.mcp_auth_token}
            onChange={(e) => handleChange('mcp_auth_token', e.target.value)}
            disabled={readOnly}
            placeholder="bearer_token_placeholder"
            rows={1}
            className="block w-full resize-y"
          />
        </div>
          </>
        )}

        {/* Allowed Intents */}
        <div className="border border-edge-gray-200 dark:border-edge-gray-700 rounded-md p-3">
          <div className="flex items-center justify-between mb-2">
            <label className="block text-xs font-medium text-ink-gray-600 dark:text-ink-gray-400">
              Allowed Intents
            </label>
            {!readOnly && (
              <CompactButton
                type="button"
                onClick={addIntent}
              >
                <Plus className="h-3 w-3" /> Add Intent
              </CompactButton>
            )}
          </div>
          <p className="mb-2 text-[11px] text-ink-gray-400">
            Actions this agent may perform. Expand an action to set its risk, approval, and rules.
          </p>
          <div className="space-y-1.5">
            {intents.map((intent, index) => {
              const isExpanded = expandedIntents.has(index);
              const riskLabel = RISK_LEVEL_OPTIONS.find((option) => option.value === intent.risk_level)?.label || intent.risk_level;
              return (
              <div key={index} className="overflow-hidden rounded border border-edge-gray-200 dark:border-edge-gray-700">
                <div className={`flex items-center gap-2 px-2 py-1.5 ${isExpanded ? "bg-fill-gray-100 dark:bg-fill-gray-800" : "bg-fill-gray-50 dark:bg-fill-gray-800/50"}`}>
                  <button
                    type="button"
                    onClick={() => toggleIntentExpand(index)}
                    aria-expanded={isExpanded}
                    className="flex min-w-0 flex-1 items-center gap-2 text-left"
                  >
                    <ChevronDown className={`h-3 w-3 shrink-0 text-ink-gray-400 transition-transform ${isExpanded ? "" : "-rotate-90"}`} />
                    <span className="shrink-0 text-xs font-semibold text-ink-gray-700 dark:text-ink-gray-200">
                      {intent.name || "Unnamed action"}
                    </span>
                    <span className="truncate text-[11px] text-ink-gray-400">{intent.description}</span>
                  </button>
                  <span className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium ${RISK_BADGE_CLASS[intent.risk_level] || RISK_BADGE_CLASS.SAFE}`}>
                    {riskLabel}
                  </span>
                  {intent.rules.length > 0 ? (
                    <span className="shrink-0 rounded bg-fill-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-ink-gray-600 dark:bg-fill-gray-700 dark:text-ink-gray-300">
                      {intent.rules.length} rule{intent.rules.length === 1 ? "" : "s"}
                    </span>
                  ) : intent.requires_approval ? (
                    <span className="flex shrink-0 items-center gap-1 rounded bg-fill-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-ink-primary">
                      <ShieldCheck className="h-3 w-3" /> Approval
                    </span>
                  ) : null}
                  {!readOnly && (
                    <div className="flex shrink-0 items-center">
                      <button
                        type="button"
                        onClick={() => addIntentRule(index)}
                        className="flex h-7 w-7 items-center justify-center text-ink-primary hover:text-ink-primary-hover-deep"
                        aria-label={`Add rule to ${intent.name || "action"}`}
                        title="Add rule"
                      >
                        <Split className="h-4 w-4 rotate-90" />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeIntent(index)}
                        className="flex h-7 w-7 items-center justify-center text-ink-red-500 hover:text-ink-red-700"
                        aria-label={`Delete ${intent.name || "action"}`}
                        title="Delete action"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>

                {isExpanded && (
                  <div className="space-y-3 border-t border-edge-gray-200 bg-fill-white px-3 py-3 dark:border-edge-gray-700 dark:bg-transparent">
                    <div className="grid grid-cols-[9rem_1fr] gap-3">
                      <div>
                        <label className="mb-1 block text-[11px] text-ink-gray-500 dark:text-ink-gray-400">Action</label>
                        <Input
                          type="text"
                          value={intent.name}
                          onChange={(e) => handleIntentChange(index, 'name', e.target.value)}
                          disabled={readOnly}
                          placeholder="e.g., list"
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-[11px] text-ink-gray-500 dark:text-ink-gray-400">Description</label>
                        <TextArea
                          value={intent.description}
                          onChange={(e) => handleIntentChange(index, 'description', e.target.value)}
                          disabled={readOnly}
                          placeholder="What this action does"
                          rows={1}
                          className="block w-full min-h-[32px] resize-y whitespace-pre-wrap"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <span className={`${STEP_LABEL_CLASS} bg-fill-blue-100 text-ink-blue-700 dark:bg-fill-blue-900/30 dark:text-ink-blue-300`}>WHEN</span>
                        <span className="text-xs text-ink-gray-500 dark:text-ink-gray-400">This action is requested:</span>
                      </div>
                      <div className={`${BRANCH_CLASS} space-y-2`}>
                        <div className="flex items-center gap-2">
                          <span className="w-28 shrink-0 text-xs text-ink-gray-600 dark:text-ink-gray-400">Base risk</span>
                          <Select
                            value={intent.risk_level}
                            onChange={(nextValue) => handleIntentChange(index, 'risk_level', nextValue)}
                            options={RISK_LEVEL_OPTIONS}
                            disabled={readOnly}
                            className="w-44"
                          />
                          <FormInfo content="Baseline risk used for this action and as the fallback risk for its rules." />
                        </div>
                        {intent.rules.length === 0 && (
                          <div className="flex items-center gap-2">
                            <span className="w-28 shrink-0 text-xs text-ink-gray-600 dark:text-ink-gray-400">Approval</span>
                            <Toggle
                              checked={intent.requires_approval}
                              onChange={(checked) => handleIntentChange(index, 'requires_approval', checked)}
                              disabled={readOnly}
                            />
                            <span className="text-xs text-ink-gray-500 dark:text-ink-gray-400">
                              {intent.requires_approval ? "Required before every command" : "Runs without approval"}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex min-w-0 items-center gap-2">
                          <span className={`${STEP_LABEL_CLASS} bg-fill-hex-ede9fe text-ink-hex-6d28d9 dark:bg-fill-hex-4c1d95/30 dark:text-ink-hex-c4b5fd`}>RULES</span>
                          <span className="truncate text-xs text-ink-gray-500 dark:text-ink-gray-400">
                            {intent.rules.length > 0
                              ? "Checked in order; the first match meeting the confidence threshold wins:"
                              : "None. The base risk and approval above apply to every command."}
                          </span>
                        </div>
                        {!readOnly && (
                          <CompactButton type="button" onClick={() => addIntentRule(index)}>
                            <Plus className="h-3 w-3" /> Add Rule
                          </CompactButton>
                        )}
                      </div>

                      {intent.rules.length > 0 && (
                        <div className={`${BRANCH_CLASS} space-y-2`}>
                          {intent.rules.map((rule, ruleIndex) => (
                            // Keyed by position, not rule.id — rule.id is user-editable text;
                            // keying on it remounts the input (and drops focus) on every keystroke.
                            <div key={ruleIndex} className="space-y-2 rounded bg-fill-gray-50 p-2 dark:bg-fill-gray-800">
                              <div className="flex items-center gap-2">
                                <span className="shrink-0 text-[11px] font-semibold text-ink-gray-500 dark:text-ink-gray-400">Rule {ruleIndex + 1}</span>
                                <div className="flex-1">
                                  <Input
                                    type="text"
                                    value={rule.id}
                                    onChange={(e) => handleIntentRuleChange(index, ruleIndex, 'id', e.target.value)}
                                    disabled={readOnly}
                                    placeholder="Rule ID"
                                  />
                                </div>
                                {!readOnly && (
                                  <div className="flex shrink-0 items-center">
                                    <button type="button" onClick={() => moveIntentRule(index, ruleIndex, -1)} disabled={ruleIndex === 0} className="p-1 text-ink-gray-500 disabled:opacity-25" title="Move rule up"><ArrowUp className="h-3.5 w-3.5" /></button>
                                    <button type="button" onClick={() => moveIntentRule(index, ruleIndex, 1)} disabled={ruleIndex === intent.rules.length - 1} className="p-1 text-ink-gray-500 disabled:opacity-25" title="Move rule down"><ArrowDown className="h-3.5 w-3.5" /></button>
                                    <button type="button" onClick={() => removeIntentRule(index, ruleIndex)} className="p-1 text-ink-red-500" title="Delete rule"><Trash2 className="h-3.5 w-3.5" /></button>
                                  </div>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <span className={`${STEP_LABEL_CLASS} shrink-0 bg-fill-blue-100 text-ink-blue-700 dark:bg-fill-blue-900/30 dark:text-ink-blue-300`}>IF</span>
                                <div className="flex-1">
                                  <Input
                                    type="text"
                                    value={rule.condition}
                                    onChange={(e) => handleIntentRuleChange(index, ruleIndex, 'condition', e.target.value)}
                                    disabled={readOnly}
                                    placeholder="Natural-language condition"
                                  />
                                </div>
                              </div>
                              <div className="flex items-start gap-2">
                                <span className={`${STEP_LABEL_CLASS} mt-1.5 shrink-0 bg-fill-green-100 text-ink-green-700 dark:bg-fill-green-900/30 dark:text-ink-green-300`}>THEN</span>
                                <div className="min-w-0 flex-1 space-y-2">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <Select
                                      value={rule.action}
                                      onChange={(nextValue) => handleIntentRuleChange(index, ruleIndex, 'action', nextValue)}
                                      options={ACTION_OPTIONS}
                                      disabled={readOnly}
                                      className="w-44"
                                    />
                                    <span className="text-xs text-ink-gray-500 dark:text-ink-gray-400">and record risk as</span>
                                    <Select
                                      value={rule.risk_level}
                                      onChange={(nextValue) => handleIntentRuleChange(index, ruleIndex, 'risk_level', nextValue)}
                                      options={RULE_RISK_OPTIONS}
                                      disabled={readOnly}
                                      className="w-36"
                                    />
                                  </div>
                                  <Input
                                    type="text"
                                    value={rule.reason}
                                    onChange={(e) => handleIntentRuleChange(index, ruleIndex, 'reason', e.target.value)}
                                    disabled={readOnly}
                                    placeholder="Reason shown to the user"
                                  />
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {intent.rules.length > 0 && (
                        <div className={`${BRANCH_CLASS} flex flex-wrap items-center gap-2`}>
                          <span className="text-xs text-ink-gray-600 dark:text-ink-gray-400">If no rule matches</span>
                          <Select
                            value={intent.default_action}
                            onChange={(nextValue) => handleIntentChange(index, 'default_action', nextValue)}
                            options={ACTION_OPTIONS}
                            disabled={readOnly}
                            className="w-44"
                          />
                          <FormInfo content="Outcome used when no rule matches or rule evaluation is incomplete or below the confidence threshold." />
                          <span className="ml-2 text-xs text-ink-gray-600 dark:text-ink-gray-400">Min. rule confidence</span>
                          <div className="w-20">
                            <Input
                              type="number"
                              min="0"
                              max="1"
                              step="0.05"
                              value={intent.minimum_rule_confidence}
                              onChange={(e) => handleIntentChange(index, 'minimum_rule_confidence', Number(e.target.value))}
                              disabled={readOnly}
                            />
                          </div>
                          <FormInfo content="Minimum model confidence required to apply a matched domain rule. Lower-confidence matches use the rule fallback." />
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              );
            })}
          </div>
        </div>

        {/* Fallback Policy */}
        <div className="border border-edge-gray-200 dark:border-edge-gray-700 rounded-md p-3">
          <div className="flex items-center justify-between mb-2">
            <label className="block text-xs font-medium text-ink-gray-600 dark:text-ink-gray-400">
              Fallback Policy (Unknown Intents)
            </label>
          </div>
          <p className="mb-2 text-[11px] text-ink-gray-400">
            Applies when the agent attempts an action that is not listed under Allowed Intents.
          </p>
          
          {/* Default Action */}
          <div className="mb-2">
            <label className="block text-xs font-medium text-ink-gray-600 dark:text-ink-gray-400 mb-1">
              Default Action
            </label>
            <Select
              value={fallbackDefaultAction}
              onChange={handleFallbackDefaultActionChange}
              options={ACTION_OPTIONS}
              disabled={readOnly}
            />
            <p className="mt-1 text-[11px] text-ink-gray-400">Used when no rule below matches.</p>
          </div>
          
          {/* Rules */}
          <div className="flex items-center justify-between mb-1">
            <label className="block text-xs font-medium text-ink-gray-600 dark:text-ink-gray-400">
              Rules
            </label>
            {!readOnly && (
              <CompactButton
                type="button"
                onClick={addFallbackRule}
              >
                <Plus className="h-3 w-3" /> Add Rule
              </CompactButton>
            )}
          </div>
          <p className="mb-2 text-[11px] text-ink-gray-400">
            {fallbackRules.length > 0
              ? "Checked in order, first match wins. Use the arrows to reorder."
              : "No rules. The default action above applies to every unknown intent."}
          </p>
          
          <div className="space-y-1.5">
            {fallbackRules.map((rule, index) => {
              const isExpanded = expandedFallbackRules.has(index);
              const actionLabel = ACTION_OPTIONS.find((option) => option.value === rule.action)?.label || rule.action;

              return (
              <div key={index} className="overflow-hidden rounded border border-edge-gray-200 dark:border-edge-gray-700">
                <div className={`flex items-center gap-2 px-2 py-1.5 ${isExpanded ? "bg-fill-gray-100 dark:bg-fill-gray-800" : "bg-fill-gray-50 dark:bg-fill-gray-800/50"}`}>
                  <button
                    type="button"
                    onClick={() => toggleFallbackRuleExpand(index)}
                    aria-expanded={isExpanded}
                    className="flex min-w-0 flex-1 items-center gap-2 text-left"
                  >
                    <ChevronDown className={`h-3 w-3 shrink-0 text-ink-gray-400 transition-transform ${isExpanded ? "" : "-rotate-90"}`} />
                    <span className="shrink-0 text-xs font-semibold text-ink-gray-700 dark:text-ink-gray-200">Rule {index + 1}</span>
                    <span className="truncate text-[11px] text-ink-gray-400">{describeFallbackCondition(rule)}</span>
                  </button>
                  <span className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium ${ACTION_BADGE_CLASS[rule.action] || ACTION_BADGE_CLASS.require_approval}`}>
                    {actionLabel}
                  </span>
                  {rule.risk_level && (
                    <span className="shrink-0 rounded bg-fill-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-ink-gray-600 dark:bg-fill-gray-700 dark:text-ink-gray-300">
                      {rule.risk_level}
                    </span>
                  )}
                  {!readOnly && (
                    <div className="flex shrink-0 items-center">
                      <button
                        type="button"
                        onClick={() => moveFallbackRule(index, -1)}
                        disabled={index === 0}
                        className="p-1 text-ink-gray-500 disabled:opacity-25"
                        aria-label={`Move fallback rule ${index + 1} up`}
                        title="Move rule up"
                      >
                        <ArrowUp className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => moveFallbackRule(index, 1)}
                        disabled={index === fallbackRules.length - 1}
                        className="p-1 text-ink-gray-500 disabled:opacity-25"
                        aria-label={`Move fallback rule ${index + 1} down`}
                        title="Move rule down"
                      >
                        <ArrowDown className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeFallbackRule(index)}
                        className="p-1 text-ink-red-500 hover:text-ink-red-700"
                        aria-label={`Delete fallback rule ${index + 1}`}
                        title="Delete fallback rule"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}
                </div>

                {isExpanded && (
                  <div className="space-y-3 border-t border-edge-gray-200 bg-fill-white px-3 py-3 dark:border-edge-gray-700 dark:bg-transparent">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <span className={`${STEP_LABEL_CLASS} bg-fill-blue-100 text-ink-blue-700 dark:bg-fill-blue-900/30 dark:text-ink-blue-300`}>IF</span>
                        <span className="text-xs text-ink-gray-500 dark:text-ink-gray-400">The unknown intent scores:</span>
                      </div>

                      <div className={`${BRANCH_CLASS} space-y-2`}>
                        <div className="flex items-center gap-2">
                          <span className="w-20 shrink-0 text-xs text-ink-gray-600 dark:text-ink-gray-400">Confidence</span>
                          <div className="w-24">
                            <Input
                              type="number"
                              value={rule.confidence_min ?? ""}
                              onChange={(e) => handleFallbackRuleChange(index, 'confidence_min', e.target.value ? parseFloat(e.target.value) : null)}
                              disabled={readOnly}
                              placeholder="min"
                              step="0.1"
                              min="0"
                              max="1"
                            />
                          </div>
                          <span className="text-xs text-ink-gray-400">to</span>
                          <div className="w-24">
                            <Input
                              type="number"
                              value={rule.confidence_max ?? ""}
                              onChange={(e) => handleFallbackRuleChange(index, 'confidence_max', e.target.value ? parseFloat(e.target.value) : null)}
                              disabled={readOnly}
                              placeholder="max"
                              step="0.1"
                              min="0"
                              max="1"
                            />
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <span className="w-20 shrink-0 text-xs text-ink-gray-600 dark:text-ink-gray-400">Severity</span>
                          <div className="w-24">
                            <Input
                              type="number"
                              value={rule.severity_min ?? ""}
                              onChange={(e) => handleFallbackRuleChange(index, 'severity_min', e.target.value ? parseFloat(e.target.value) : null)}
                              disabled={readOnly}
                              placeholder="min"
                              step="0.1"
                              min="0"
                              max="1"
                            />
                          </div>
                          <span className="text-xs text-ink-gray-400">to</span>
                          <div className="w-24">
                            <Input
                              type="number"
                              value={rule.severity_max ?? ""}
                              onChange={(e) => handleFallbackRuleChange(index, 'severity_max', e.target.value ? parseFloat(e.target.value) : null)}
                              disabled={readOnly}
                              placeholder="max"
                              step="0.1"
                              min="0"
                              max="1"
                            />
                          </div>
                        </div>
                        <p className="text-[11px] text-ink-gray-400">Leave a bound empty for no limit.</p>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <span className={`${STEP_LABEL_CLASS} bg-fill-green-100 text-ink-green-700 dark:bg-fill-green-900/30 dark:text-ink-green-300`}>THEN</span>
                        <span className="text-xs text-ink-gray-500 dark:text-ink-gray-400">Perform action:</span>
                      </div>

                      <div className={`${BRANCH_CLASS} space-y-2`}>
                        <Select
                          value={rule.action}
                          onChange={(nextValue) => handleFallbackRuleChange(index, 'action', nextValue)}
                          options={ACTION_OPTIONS}
                          disabled={readOnly}
                          className="w-52"
                        />

                        {(rule.action === 'require_approval' || rule.action === 'block') && (
                          <div className="flex items-center gap-2">
                            <span className="w-20 shrink-0 text-xs text-ink-gray-600 dark:text-ink-gray-400">Risk level</span>
                            <div className="w-52">
                              <Input
                                type="text"
                                value={rule.risk_level}
                                onChange={(e) => handleFallbackRuleChange(index, 'risk_level', e.target.value)}
                                disabled={readOnly}
                                placeholder="e.g., HIGH_RISK"
                              />
                            </div>
                          </div>
                        )}

                        {rule.action === 'require_approval' && (
                          <div className="flex items-center gap-2">
                            <span className="w-20 shrink-0 text-xs text-ink-gray-600 dark:text-ink-gray-400">Message</span>
                            <div className="min-w-0 flex-1">
                              <Input
                                type="text"
                                value={rule.confirmation_text}
                                onChange={(e) => handleFallbackRuleChange(index, 'confirmation_text', e.target.value)}
                                disabled={readOnly}
                                placeholder="Confirmation message shown to the user"
                              />
                            </div>
                          </div>
                        )}

                        {rule.action === 'block' && (
                          <div className="flex items-center gap-2">
                            <span className="w-20 shrink-0 text-xs text-ink-gray-600 dark:text-ink-gray-400">Reason</span>
                            <div className="min-w-0 flex-1">
                              <Input
                                type="text"
                                value={rule.reason}
                                onChange={(e) => handleFallbackRuleChange(index, 'reason', e.target.value)}
                                disabled={readOnly}
                                placeholder="Reason shown when blocking"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )})}
          </div>
        </div>

        {/* Rate Limits */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-ink-gray-600 dark:text-ink-gray-400 mb-1">
              Per User Limit
            </label>
            <Input
              type="number"
              value={formData.per_user_limit}
              onChange={(e) => handleChange('per_user_limit', parseInt(e.target.value) || 0)}
              disabled={readOnly}
              min="0"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-ink-gray-600 dark:text-ink-gray-400 mb-1">
              Per Org Limit
            </label>
            <Input
              type="number"
              value={formData.per_org_limit}
              onChange={(e) => handleChange('per_org_limit', parseInt(e.target.value) || 0)}
              disabled={readOnly}
              min="0"
            />
          </div>
        </div>

        {/* Allowed Roles is intentionally absent: agent access is enforced by group policy
            (allowed_agents/denied_agents), not by any role list stored in this file. */}
        <div className="flex items-start gap-2 rounded border border-edge-blue-200 bg-fill-blue-50 px-3 py-2.5 dark:border-edge-blue-800 dark:bg-fill-blue-900/20">
          <Users className="mt-0.5 h-4 w-4 shrink-0 text-ink-blue-600 dark:text-ink-blue-400" />
          <p className="text-xs text-ink-blue-800 dark:text-ink-blue-300">
            Who can use this agent is managed under <span className="font-semibold">Settings → Groups</span>.
          </p>
        </div>

        {policyMetadataItems.length > 0 && (
          <div className="rounded border border-edge-gray-200 bg-fill-gray-50 px-4 py-3 dark:border-edge-gray-700 dark:bg-fill-gray-800/50">
            <div className="mb-2.5 flex items-center gap-1.5">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-ink-gray-500 dark:text-ink-gray-400">
                Policy metadata
              </span>
              <FormInfo content="Recorded automatically by the platform. Not editable. All times are UTC." />
            </div>
            <dl className="flex flex-wrap gap-x-10 gap-y-4">
              {policyMetadataItems.map((item) => (
                <div key={item.label} className="min-w-0">
                  <dt className="mb-1 text-[10px] uppercase tracking-wide text-ink-gray-400">{item.label}</dt>
                  <dd
                    className={`truncate text-xs text-ink-gray-700 dark:text-ink-gray-300 ${item.mono ? "font-mono" : ""}`}
                    title={item.title || item.value}
                  >
                    {item.value}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        )}

        {/* Info */}
        <div className="text-xs pt-2 border-t border-edge-gray-200 dark:border-edge-gray-700 space-y-2">
          <div className="text-ink-gray-500 dark:text-ink-gray-400">
            <p>💡 Default intent (read) and fallback policy included automatically.</p>
            <p className="mt-1">For advanced configuration (intents, fallback rules), switch to JSON view.</p>
          </div>
          <div className="flex items-start gap-2 p-2 bg-fill-red-50 dark:bg-fill-red-900/20 border border-edge-red-200 dark:border-edge-red-800 rounded">
            <span className="text-ink-red-600 dark:text-ink-red-400">⚠️</span>
            <p className="text-ink-red-700 dark:text-ink-red-300 font-medium">
              <strong>CONFIDENTIAL:</strong> This is an enterprise classified policy. Do not share outside authorized personnel.
            </p>
          </div>
        </div>
        </div>
      ) : (
        <JsonView
          value={value}
          onChange={onChange}
          onError={onError}
          fileType={fileType}
          readOnly={readOnly}
        />
      )}
    </div>
  );
};

export default PolicyFormView;
